// Standalone risk calculation functions extracted from RiskModel class.
// Each function accepts a `model` parameter (the RiskModel instance) to access
// configuration like lowestRiskLevel, riskLevels, hybridSettings, invertedConfig, riskThresholds.

export function calculateWeightedRisk(model, countryData, indices, weights) {
  let totalWeightedScore = 0;
  let totalWeight = 0;
  const details = {
    indexContributions: [],
    totalWeight: 0,
    weightedAverage: 0,
    calculation: 'Weighted average: (score1 × weight1 + score2 × weight2 + ...) ÷ (weight1 + weight2 + ...)'
  };

  for (const index of indices) {
    const value = countryData[index.id];
    if (value !== undefined && value !== null) {
      const weight = weights[index.id] || 1;
      totalWeight += weight;
    }
  }

  for (const index of indices) {
    const value = countryData[index.id];
    const weight = weights[index.id] || 1;

    if (value === undefined || value === null) {
      details.indexContributions.push({
        indexName: index.name,
        value: 'Missing',
        normalizedScore: 0,
        weight: weight,
        weightedContribution: 0,
        calculationStep: `0 × ${weight} = 0`
      });
      continue;
    }

    let normalizedScore = 0;

    if (index.config.type === 'BOOLEAN') {
      normalizedScore = (value === true || value === 1) ? 100 : 0;
    } else if (index.config.type === 'SCORING') {
      const rawScore = Number(value);
      const maxValue = index.config.scale?.maxValue || 100;
      const isInverted = index.config.scale?.isInverted;
      const isNormalized = index.config.scale?.normalizeToScale;

      if (isNormalized) {
        const minValue = index.config.scale?.minValue || 0;
        const range = maxValue - minValue;
        normalizedScore = range > 0 ? ((rawScore - minValue) / range) * 100 : 0;
      } else {
        normalizedScore = rawScore;
      }

      if (isInverted) {
        normalizedScore = 100 - normalizedScore;
      }
    }

    const weightedContribution = normalizedScore * weight;
    totalWeightedScore += weightedContribution;

    details.indexContributions.push({
      indexName: index.name,
      value: value,
      normalizedScore: normalizedScore,
      weight: weight,
      weightedContribution: weightedContribution,
      calculationStep: `${normalizedScore} × ${weight} = ${weightedContribution}`
    });
  }

  const finalScore = totalWeight > 0 ? totalWeightedScore / totalWeight : 0;

  details.totalWeight = totalWeight;
  details.totalWeightedScore = totalWeightedScore;
  details.finalCalculation = `${totalWeightedScore} ÷ ${totalWeight} = ${finalScore?.toFixed(2) || 'N/A'}`;

  const level = model.mapScoreToRiskLevel(finalScore || 0);

  return {
    level: level,
    score: Math.round((finalScore || 0) * 100) / 100,
    details: details
  };
}

export function calculateHybridRisk(model, countryData, indices, weights) {
  const hybridSettings = model.hybridSettings || { strategy: 'max', indexModes: {} };

  const deterministicIndices = indices.filter(index =>
    hybridSettings.indexModes[index.id] === 'deterministic'
  );
  const weightedIndices = indices.filter(index =>
    hybridSettings.indexModes[index.id] !== 'deterministic'
  );

  const deterministicResult = deterministicIndices.length > 0
    ? model.calculateDeterministicRisk(countryData, deterministicIndices)
    : { level: model.lowestRiskLevel, score: 0 };

  const weightedResult = weightedIndices.length > 0
    ? calculateWeightedRisk(model, countryData, weightedIndices, weights)
    : { level: model.lowestRiskLevel, score: 0 };

  switch (hybridSettings.strategy) {
    case 'max':
      return model.getRiskLevel(deterministicResult.score) === 'High' ||
             model.getRiskLevel(weightedResult.score) === 'High'
        ? { level: 'High', score: Math.max(deterministicResult.score, weightedResult.score) }
        : model.getRiskLevel(deterministicResult.score) === 'Medium' ||
          model.getRiskLevel(weightedResult.score) === 'Medium'
        ? { level: 'Medium', score: Math.max(deterministicResult.score, weightedResult.score) }
        : { level: model.lowestRiskLevel, score: Math.max(deterministicResult.score, weightedResult.score) };

    case 'override':
      return deterministicResult.level === 'High'
        ? deterministicResult
        : weightedResult;

    case 'additive': {
      const combinedScore = deterministicResult.score + weightedResult.score;
      const threshold = hybridSettings.additiveThreshold || 150;
      return combinedScore >= threshold
        ? { level: 'High', score: combinedScore }
        : combinedScore >= 100
        ? { level: 'Medium', score: combinedScore }
        : { level: model.lowestRiskLevel, score: combinedScore };
    }

    case 'threshold': {
      const weightedThreshold = hybridSettings.weightedThreshold || 50;
      if (deterministicResult.level === 'High' && weightedResult.score >= weightedThreshold) {
        return { level: 'High', score: Math.max(deterministicResult.score, weightedResult.score) };
      }
      return model.getRiskLevel(deterministicResult.score) === 'Medium' ||
             model.getRiskLevel(weightedResult.score) === 'Medium'
        ? { level: 'Medium', score: Math.max(deterministicResult.score, weightedResult.score) }
        : { level: model.lowestRiskLevel, score: Math.max(deterministicResult.score, weightedResult.score) };
    }

    default:
      return deterministicResult.level === 'High'
        ? deterministicResult
        : weightedResult;
  }
}

export function calculateInvertedRisk(model, countryData, indices, weights) {
  let totalScore = model.invertedConfig.startingScore;
  const deductions = [];

  for (const index of indices) {
    const value = countryData[index.id];
    const weight = weights[index.id] || 1;

    if (value === undefined || value === null) {
      continue;
    }

    let deduction = 0;

    if (index.config.type === 'BOOLEAN') {
      if (value === false || value === 0) {
        deduction = model.invertedConfig.booleanDeduction * weight;
      }
    } else if (index.config.type === 'SCORING') {
      const rawScore = Number(value);
      const maxValue = index.config.scale?.maxValue || 100;
      const isInverted = index.config.scale?.isInverted;

      let normalizedScore = rawScore;
      if (index.config.scale?.normalizeToScale) {
        const minValue = index.config.scale?.minValue || 0;
        const range = maxValue - minValue;
        normalizedScore = range > 0 ? ((rawScore - minValue) / range) * 100 : 0;
      }

      let scoreForDeduction = normalizedScore;
      if (!isInverted) {
        scoreForDeduction = 100 - normalizedScore;
      } else {
        scoreForDeduction = normalizedScore;
      }

      deduction = (scoreForDeduction / 100) * model.invertedConfig.maxScoringDeduction * weight;
    }

    if (deduction > 0) {
      totalScore -= deduction;
      deductions.push({
        indexName: index.name,
        value: value,
        deduction: Math.round(deduction * 100) / 100,
        weight: weight
      });
    }
  }

  totalScore = Math.max(model.invertedConfig.minimumScore, totalScore);

  return {
    level: model.mapScoreToRiskLevel(totalScore),
    score: Math.round(totalScore * 100) / 100,
    details: {
      startingScore: model.invertedConfig.startingScore,
      totalDeductions: model.invertedConfig.startingScore - totalScore,
      deductions: deductions
    }
  };
}
